import React, { forwardRef, useState } from "react";
import { Password, PasswordProps } from "primereact/password";
import { classNames } from "primereact/utils";
import { DashlyFieldShell } from "../shared/DashlyFieldShell";
import { DashlyControlSize, dashlyControlSizeClass } from "../shared/dashlyControlSize";
import "../shared/dashlyFormControl.scss";
import "./DashlyPasswordInput.scss";

const MASK_FILL_CHARACTER = "\u2022";

export type DashlyPasswordInputProps = Omit<PasswordProps, "inputRef"> & {
    /** Control height scale. Default `md` (2.5rem). */
    controlSize?: DashlyControlSize;
    label?: React.ReactNode;
    labelInfo?: string;
    hint?: React.ReactNode;
    required?: boolean;
    containerClassName?: string;
    /** When masked and value still equals `maskBaselineValue`, show this many dots instead of the real length. */
    fixedMaskLength?: number;
    /** Original loaded value; fixed-length masking applies only while the current value is unchanged. */
    maskBaselineValue?: string;
    /** Called before revealing a fixed-length mask (e.g. fetch stored secret from API). */
    onBeforeFixedMaskReveal?: () => void | Promise<void>;
    /** When false, focus does not reveal a fixed mask (toggle only). Default true unless `onBeforeFixedMaskReveal` is set. */
    revealOnFocus?: boolean;
};

const DashlyPasswordInput = forwardRef<HTMLInputElement, DashlyPasswordInputProps>(function DashlyPasswordInput(
    {
        controlSize = "md",
        label,
        labelInfo,
        hint,
        required,
        containerClassName,
        className,
        inputClassName,
        inputId,
        id,
        value,
        onChange,
        onFocus,
        icon,
        fixedMaskLength,
        maskBaselineValue,
        onBeforeFixedMaskReveal,
        revealOnFocus,
        disabled,
        ...rest
    },
    ref,
) {
    const resolvedInputId = inputId ?? id;
    const [revealed, setRevealed] = useState(false);
    const [isRevealing, setIsRevealing] = useState(false);
    const resolvedRevealOnFocus = revealOnFocus ?? onBeforeFixedMaskReveal == null;

    const stringValue = value == null ? "" : String(value);
    const baselineValue = maskBaselineValue ?? "";
    const hasUnchangedSecret =
        baselineValue.length > 0 &&
        stringValue.length > 0 &&
        stringValue === baselineValue;
    const useFixedMaskDisplay =
        fixedMaskLength != null &&
        fixedMaskLength > 0 &&
        !revealed &&
        hasUnchangedSecret;
    const displayedValue = useFixedMaskDisplay
        ? MASK_FILL_CHARACTER.repeat(fixedMaskLength)
        : stringValue;

    const revealFixedMask = async () => {
        if (!onBeforeFixedMaskReveal) {
            setRevealed(true);
            return;
        }

        setIsRevealing(true);
        try {
            await onBeforeFixedMaskReveal();
            setRevealed(true);
        } finally {
            setIsRevealing(false);
        }
    };

    const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
        if (useFixedMaskDisplay && resolvedRevealOnFocus && !isRevealing) {
            void revealFixedMask();
        }
        onFocus?.(event);
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (useFixedMaskDisplay) {
            const maskString = MASK_FILL_CHARACTER.repeat(fixedMaskLength!);
            if (event.target.value === maskString) {
                return;
            }
            setRevealed(true);
        }
        onChange?.(event);
    };

    // Replace the icon's onClick (do not wrap) so PrimeReact positioning CSS stays intact.
    // PrimeReact also puts toggleMask on the element — calling options.onClick alone would double-fire.
    const handleToggleMaskIcon = (options: { onClick?: React.MouseEventHandler; element: React.ReactNode }) => {
        const { onClick, element } = options;

        if (isRevealing) {
            return (
                <i
                    className="pi pi-spin pi-spinner dashly-password-input__reveal-spinner"
                    aria-hidden
                />
            );
        }

        if (!React.isValidElement(element)) {
            return element;
        }

        const handleIconActivate = (event: React.MouseEvent | React.KeyboardEvent) => {
            if (isRevealing) {
                return;
            }
            if (useFixedMaskDisplay) {
                void (async () => {
                    try {
                        await revealFixedMask();
                        onClick?.(event as React.MouseEvent);
                    } catch {
                        // Reveal failed — keep PrimeReact masked.
                    }
                })();
                return;
            }
            setRevealed((current) => !current);
            onClick?.(event as React.MouseEvent);
        };

        return React.cloneElement(
            element as React.ReactElement<{
                onClick?: React.MouseEventHandler;
                onKeyDown?: React.KeyboardEventHandler;
            }>,
            {
                onClick: (event: React.MouseEvent) => {
                    handleIconActivate(event);
                },
                onKeyDown: (event: React.KeyboardEvent) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        handleIconActivate(event);
                    }
                },
            },
        );
    };

    const control = (
        <div
            className={classNames("dashly-password-input-wrap", dashlyControlSizeClass(controlSize))}
            aria-busy={isRevealing || undefined}
        >
            <Password
                {...rest}
                id={resolvedInputId}
                inputRef={ref}
                value={displayedValue}
                onChange={handleChange}
                onFocus={handleFocus}
                disabled={disabled || isRevealing}
                icon={icon ?? (fixedMaskLength != null || isRevealing ? handleToggleMaskIcon : undefined)}
                className={classNames("dashly-password-input", "w-full", className)}
                inputClassName={classNames("dashly-password-input__field", inputClassName)}
            />
        </div>
    );

    if (!label) {
        return control;
    }

    return (
        <DashlyFieldShell
            label={label}
            labelInfo={labelInfo}
            hint={hint}
            htmlFor={resolvedInputId}
            required={required}
            className={containerClassName}
        >
            {control}
        </DashlyFieldShell>
    );
});

DashlyPasswordInput.displayName = "DashlyPasswordInput";

export { DashlyPasswordInput };
